import { LanguageConfigurations } from './LanguageConfigurations';
import { NumberConfigurations } from './NumberConfigurations';
import { FontAwesomeConfigurations } from './FontAwesomeConfiguration';
import DefaultConfigurations from './DefaultConfigurations';
import FirehoseAnalyticsConfigurations from './FirehoseAnalyticsConfigurations';
import LiveChat from '../../ui/objects/livechat/LiveChat';
import { IoTThingsGraph } from 'aws-sdk';

interface AppPlatformData {
  Platform: {
    Id: number; // GUID
    Name: string;
    Sites: [
      {
        Id: number;
        Base: number;
        Configuration: number;
        Name: string;
        Path: string;
        Type: ENUM;
        Tags: [
          {
            TagId: number;
            TagName: string;
            TypeId: number;
            TypeName: string;
          },
        ];
        Attributes: [
          {
            AttributeId: number;
            Key: string;
            Value: string;
          },
        ];
      },
    ];
  };
}

interface AppConfigurationObject {
  bannerContent: any;
  base_config: [{}];
  buckets_list: {};
  cached: boolean;
  config_config: {};
  config_date: string; //2022-04-27 22:25:15
  config_id: number;
  config_lang: {};
  config_name: string;
  config_version: number;
  leaderboard: any;
  platform: [
    {
      platform_id: number;
      platform_name: string;
    },
  ];
  site_types: [];
  success: boolean | string;
  tags_list: {};
  tag_types: {
    items: [];
  };
  theme_id: number;
  theme_is_default: boolean;
  theme_name: string;
  theme_path: string;
  themes_to_attributes: [];
  themes_to_tags: [];
  theme_type: string;
  user_dimensions_json: Array<Object>;
  user_edited: string;
}

interface AppConfigurations {
  config: Array<string>;
  buckets_list: Array<string>;
  config_config: Array<string>;
  config_lang: Array<string>;
  tags_list: Array<string>;
  user_dimensions: Array<string>;
  user_dimensions_json: Array<string>;
}

export default class AppConfiguration {
  public defaultConfigs: DefaultConfigurations;
  public firehoseAnalyticsConfigs: FirehoseAnalyticsConfigurations;
  private _appConfigurations: AppConfigurationObject;
  private _appPlatformData: AppPlatformData;
  private _appNavigation: [];
  constructor() {
    this._appConfigurations = {
      platform: [
        {
          platform_id: -1,
          platform_name: 'Not Set',
        },
      ],
    };
    this._appPlatformData = window.PLATFORM_DATA = {
      Platform: {
        Id: -1,
        Name: 'Not Set',
        Sites: [
          {
            Id: -1,
            Base: -1,
            Configuration: -1,
            Name: 'Not Set',
            Type: 'Not Set',
            Tags: [],
            Attributes: [],
          },
        ],
      },
    };
    this._appNavigation = [];
    this.setDefaultConfiguration();
  }

  public getUserDimensionsConfig(): Array<Object> {
    return this._appConfigurations.user_dimensions_json;
  }

  public getAppNavigation(): Array<Object> {
    return this._appNavigation;
  }

  public getPlatformId(): number {
    return this._appPlatformData.Platform.Id;
  }

  public getPlatformPath(): string {
    return this._appPlatformData.Platform.Sites[0].Path;
  }

  public setDefaultConfiguration(): void {
    this._appNavigation = [];
    this.defaultConfigs = new DefaultConfigurations();
    this.firehoseAnalyticsConfigs = new FirehoseAnalyticsConfigurations();
    this.defaultConfigs.setDefaultConfigs();
    this.firehoseAnalyticsConfigs.setAnalyticsConfigs();
  }

  /**
   * setupConfiguration
   * @returns {Promise<AppConfigurationObject>} Promise which resolves with successful api_config_get call
   * - this will eventually have CrowdHub_App.CONFIG deprecated
   */
  public setupConfiguration(): Promise<AppConfigurationObject> {
    return new Promise((resolve, reject) => {
      App.INSTANCE.mServices.GetConfigs(
        null,
        result => {
          // CrowdHub_App.INSTANCE.CONFIG_CONTENT = result.config;
          let config = result.config;
          this._appConfigurations = result.config;
          // CrowdHub_App.INSTANCE.CONFIG_CONTENT.buckets_list =
          this._appConfigurations.buckets_list = JSON.parse(config.buckets_list);
          // CrowdHub_App.INSTANCE.CONFIG_CONTENT.config_config =
          this._appConfigurations.config_config = JSON.parse(config.config_config);
          // CrowdHub_App.INSTANCE.CONFIG_CONTENT.config_lang =
          this._appConfigurations.config_lang = JSON.parse(config.config_lang);
          // CrowdHub_App.INSTANCE.CONFIG_CONTENT.tags_list
          this._appConfigurations.tags_list = JSON.parse(config.tags_list);
          this._appConfigurations.user_dimensions_json = JSON.parse(config.user_dimensions).items;
          this._processConfigs();
          Resources.prependBucket();

          CrowdHub_App.INSTANCE.CONFIG_CONTENT = this._appConfigurations;
          CrowdHub_App.INSTANCE.CONFIG_CONTENT.user_dimensions = {};
          CrowdHub_App.INSTANCE.CONFIG_CONTENT.user_dimensions_json = this._appConfigurations.user_dimensions_json;
          $.each(CrowdHub_App.INSTANCE.CONFIG_CONTENT.user_dimensions_json, function (index, dimension) {
            CrowdHub_App.INSTANCE.CONFIG_CONTENT.user_dimensions[dimension.key] = dimension.id;
          });
          resolve(this._appConfigurations);
        },
        error => {
          let confirmTitle = 'There was an issue loading the site configurations!';
          let confirmMessage = HGEN.tree({
            style: '',
            interior: `Click OK to refresh your tab/app and try again. If the issue persists<br>${HGEN.span({
              style: 'light',
              interior: CrowdHub_App.LANG.CONTACT_AT,
            })} ${HGEN.link({
              style: 'link',
              href: 'mailto: ' + CrowdHub_App.LANG.HELPDESK_EMAIL,
              interior: CrowdHub_App.LANG.HELPDESK_EMAIL,
            })}`,
          });
          var confirmPopup = new ConfirmPopupPanel();
          confirmPopup.showOkConfirm(confirmTitle, confirmMessage, () => {
            App.INSTANCE.removeLocalStorageSiteValues();
            App.INSTANCE.reloadLocation();
          });
          reject(error);
        },
      );
    });
  }

  // TODO refactor
  private _processConfigs(): void {
    console.log('+++processing configs');
    this._appPlatformData.Platform = {
      Id: this._appConfigurations.platform[0] != undefined ? this._appConfigurations.platform[0]?.platform_id : -1,
      Name:
        this._appConfigurations.platform[0] != undefined
          ? this._appConfigurations.platform[0].platform_name
          : 'Not Set',
      Sites: [
        {
          Id: Number(this._appConfigurations.theme_id),
          Base: this._appConfigurations.base_config[0].config_id,
          Path: this._appConfigurations.theme_path,
          Configuration: Number(this._appConfigurations.config_id),
          Name: this._appConfigurations.theme_name,
          Type: this._appConfigurations.theme_type,
          Tags: [],
          Attributes: [],
        },
      ],
    };

    var baseConfig = JSON.parse(this._appConfigurations.base_config[0].config_config);

    $.each(baseConfig, function (index, element) {
      CrowdHub_App.CONFIG[index] = element;
    });

    var baseLangs = JSON.parse(this._appConfigurations.base_config[0].config_lang);
    $.each(baseLangs, function (index, element) {
      CrowdHub_App.LANG[index] = element;
    });

    // then go thru all the site configs and override any crowdhub config values with server values
    for (var p in this._appConfigurations.config_config) {
      if (p == 'NAVIGATION') {
        this._appNavigation = this._appConfigurations.config_config[p].filter(navItem => {
          return navItem.as != '';
        });
        this._appNavigation.forEach(nav => {
          CrowdHub_App.CONFIG[p].push(nav);
        });
      } else if (p == 'CSS_RULES') {
        this._appConfigurations.config_config[p].forEach(css => {
          CrowdHub_App.CONFIG[p].push(css);
        });
      } else {
        // if the item is not in the blacklist
        if (!CONFIG_BLACKLIST.includes(p)) {
          CrowdHub_App.CONFIG[p] = this._appConfigurations.config_config[p];
        }
      }
    }

    //things like popups that don't belong in config
    this._addDefaultRoutes();

    this._appConfigurations.themes_to_tags.forEach(tagItem => {
      if (tagItem.theme_id == this._appConfigurations.theme_id) {
        this._appPlatformData.Platform.Sites[0].Tags.push({
          TagId: tagItem.tag_id,
          TagName: tagItem.tag_name,
          TypeId: tagItem.tag_type_id,
          TypeName: tagItem.tag_type_name,
        });
      }
    });

    this._appConfigurations.themes_to_attributes.forEach(attributeItem => {
      if (attributeItem.theme_id == this._appConfigurations.theme_id) {
        this._appPlatformData.Platform.Sites[0].Attributes.push({
          AttributeId: attributeItem.t_to_a_id,
          Key: attributeItem.attribute_key,
          Value: attributeItem.attribute_value,
        });
      }
    });

    // give a global place to grab tags rather than loading them constantly
    CrowdHub_App.CONFIG.TAGS = this._appConfigurations.tags_list.items;
    if (this._appConfigurations.tag_types) {
      CrowdHub_App.CONFIG.TAGTYPES = this._appConfigurations.tag_types.items;
    }

    $.each(this._appConfigurations.config_lang, function (index, element) {
      CrowdHub_App.LANG[index] = element;
    });

    // check so that we dont append the path if they use subdomains
    if (this._appPlatformData.Platform.Sites[0].Path == window.location.host) {
      this._appPlatformData.Platform.Sites[0].Path = '';
    }

    //in cordova there is no window host, check this way
    if (new URL(CrowdHub_App.CONFIG.BASE_URL).hostname == this._appPlatformData.Platform.Sites[0].Path) {
      this._appPlatformData.Platform.Sites[0].Path = '';
    }

    window.PLATFORM_DATA = this._appPlatformData;
    mLocalStorage.set('site_path', window.PLATFORM_DATA.Platform.Sites[0].Path);

    if (App.INSTANCE.liveChat) {
      // we are refreshing we dont want a new chat just set the changes
      App.INSTANCE.liveChat.refreshLiveChatParams();
    } else {
      App.INSTANCE.liveChat = new LiveChat();
      console.log('+++++ App.INSTANCE.liveChat');
    }

    console.log('+++ processconfigs site path ' + window.PLATFORM_DATA.Platform.Sites[0].Path);

    console.log('+++ processconfigs nav length ' + CrowdHub_App.CONFIG.NAVIGATION.length);
    // we are checking the login mode here as well since we route between subdomains and need to know before continuing
    if (!App.INSTANCE.CurrentUser.IsLoggedIn()) {
      switch (CrowdHub_App.CONFIG.LOGIN_MODE) {
        case CrowdHub_App.LOGINMODE_SAML:
        case CrowdHub_App.LOGINMODE_OAUTH:
          localStorage.setItem('sso-login', 'true');
          break;
      }
    }

    //whitelabel
    CrowdHub_App.CONFIG.oauth_login_url = this._appConfigurations.oauth_login_url;
    CrowdHub_App.CONFIG.oauth_redirect_url = this._appConfigurations.oauth_redirect_url;

    if (this._appConfigurations.leaderboard) {
      var c = App.INSTANCE.contentFactory(this._appConfigurations.leaderboard);
      CrowdHub_App.CONFIG.leaderboard = c;
    }

    if (this._appConfigurations.bannerContent) {
      var c = App.INSTANCE.contentFactory(this._appConfigurations.bannerContent);
      CrowdHub_App.CONFIG.bannerContent = c;
    }

    this._setupThemConfigurations();
  }

  private _addDefaultRoutes() {
    var defaultRoutes = [
      {
        name: 'cordovalogin',
        type: 'cordovalogin',
        as: 'navigate.cordovalogin',
        ignoreRoute: true,
        path: '/cordovalogin',
      },
      {
        name: 'Login Modal',
        inNav: false,
        inTopNav: false,
        inBottomNav: false,
        ignoreRoute: true,
        type: 'loginmodal',
        path: '/loginmodal',
        as: 'navigate.loginmodal',
        loginMode: 'all',
        accountMode: 'all',
      },
      {
        name: 'Connection Preview',
        inNav: false,
        inTopNav: false,
        inBottomNav: false,
        type: 'connectionpreview',
        path: '/quickview/connection/:id',
        as: 'navigate.connectionpreview',
        loginMode: 'all',
        accountMode: 'all',
      },
      {
        name: 'Notification Preferences',
        inNav: false,
        inTopNav: false,
        inBottomNav: false,
        type: 'notificationprefs',
        path: '/#notificationpreferences',
        as: 'navigate.notificationpreferences',
        loginMode: 'loggedin',
        accountMode: 'all',
      },
      {
        name: 'SSO Popup',
        inNav: false,
        inTopNav: false,
        inBottomNav: false,
        type: 'ssopopup',
        path: '/ssopopup',
        as: 'navigate.ssopopup',
        loginMode: 'all',
        accountMode: 'all',
      },
    ];

    defaultRoutes.forEach(route => {
      CrowdHub_App.CONFIG.NAVIGATION.push(route);
    });
  }

  private _setupThemConfigurations() {
    // TODO move
    if (CrowdHub_App.CONFIG.DARK_MODE) {
      $('body').addClass('darkMode');
      Resources.resources.ARROWLEFTDARKGREY = {
        src: Resources.UIPATH + 'ArrowLeft-White.svg',
      };
      Resources.resources.CLOSECROSS = {
        src: Resources.UIPATH + 'Icon_Close_X_White.svg',
      };
      Resources.resources.ICON_ARROW_DOWN_BLACK = {
        src: Resources.UIPATH + 'Icon_CloseArrow.svg',
      };
    }
  }
}

const CONFIG_BLACKLIST = [
  'ALLOW_PRIVATE_INVITES_BY_ALL',
  'ARCHIVE_GREEN_ROOM',
  'AWS_IVS_RECONNECT_TIMEOUT_IN_MS',
  'BASE_URL',
  'BROADCAST_TIMEOUT_BEFORE_LEAVE_IN_MILLISECONDS',
  'BROWSER_ERRORS',
  'CHECK_NETWORK_STATS',
  'CHECK_PUBLISHER_STATS',
  'CHECK_STREAM_STATS',
  'CME_PROGRESS_BAR_STROKE_WIDTH',
  'NETWORK_STATUS_CHECK_URL',
  'SALT',
  'PUBNUB_UUID_PREFIX',
  'PUBNUB_PERSONALCHANNEL_PREFIX',
  'PUBNUB_GROUPCHANNEL_PREFIX',
  'PUBNUB_GLOBALROOM',
  'PUBNUB_PUBLISHKEY',
  'PUBNUB_SUBSCRIBEKEY',
  'FROALA_KEY',
  'DATA_API_KEY',
  'FACEBOOK_REDIRECT_URL',
  'S3_IMAGEBUCKET_LOCATION',
  'S3_URL',
  'URL_ITEM',
  'URL_ITEM_SLUG',
  'SHARE_URL',
  'JWT_ALGORITHM',
  'JWT_SIGNINGKEY',
  'MIC_CHECK_URL',
  'ON_REGISTER_COMPLETE',
  'NETWORK_STATUS_CHECK_NUM_RUN_TIMES',
  'FLUSH_ANALYTICS_QUEUE',
  'PANEL_CACHE_SECONDS',
  'SCHEDULE_NOTIFIER_MAX',
  'SCHEDULE_NOTIFIER_MIN',
  'POPUP_NOTIFIER_MAX',
  'POPUP_NOTIFIER_MIN',
  'CLIP_PREROLLS_AT_SECONDS',
  'CLIP_PREROLLS_AT_SECONDS_MODERATORS',
  'ENCRYPTPASSWORD',
  'FACEBOOKENABLED',
  'JAVASCRIPT_TAGS',
  'LET_MODERATORS_IN_SECONDS',
  'MODERATOR_CAN_REQUEST_ATTENDEE_VIDEO',
  'NETWORK_STATS_FREQ',
  'NOTIFICATION_REFRESHTIME',
  'PACKETLOSS_ARRAY_LENGTH',
  'PACKETLOSS_PERCENTAGE',
  'PARTNER_METADATA_IDS',
  'PREROLL_VIDEO_TIME_OFFSET_THRESHOLD_MILLISECONDS',
  'PRIVATE_MEETING_INVITATION_MAXIMUM',
  'PRIVATE_ROOMS_RECORDED',
  'PUBLISHER_STATS_FREQ',
  'RSVP_WAITLIST',
  'SESSION_IS_OVER_REDIRECT_TIME_IN_SECONDS',
  'SNAP_PREROLL_TO_LIVE',
  'SNAP_PLAYERS_TO_LIVE',
  'SUBSCRIBER_STATS_FREQ',
  'SUPPRESSCONTENTLOADING',
  'USING_PAGINATED_LAYOUT',
  'UNLOCKING_ENABLED',
];
